import { Fragment } from 'react';
import { Box, Link, Typography } from '@mui/material';

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;

function splitTrailingPunctuation(urlText) {
  let url = urlText;
  let trailingText = '';

  while (url && /[.,!?;:]/.test(url.slice(-1))) {
    trailingText = `${url.slice(-1)}${trailingText}`;
    url = url.slice(0, -1);
  }

  return { url, trailingText };
}

function linkifyText(text) {
  const normalizedText = typeof text === 'string' ? text : String(text ?? '');

  return normalizedText.split(URL_PATTERN).map((part, index) => {
    if (!/^https?:\/\//i.test(part)) {
      return <Fragment key={`text-${index}`}>{part}</Fragment>;
    }

    const { url, trailingText } = splitTrailingPunctuation(part);

    return (
      <Fragment key={`url-${index}`}>
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          sx={{ overflowWrap: 'anywhere' }}
        >
          {url}
        </Link>
        {trailingText}
      </Fragment>
    );
  });
}

export default function EpisodeSummaries({
  summary,
  aiSummary,
  summaryText = summary,
  shouldTruncate = false,
  expanded = false,
  onToggleSummary,
  compact = false
}) {
  if (!summary && !aiSummary) {
    return null;
  }

  const bodySx = compact
    ? {
        fontSize: { xs: '0.95rem', sm: '1rem' },
        lineHeight: { xs: 1.55, sm: 1.6 }
      }
    : {
        fontSize: { xs: '1rem', sm: '1.0625rem' },
        lineHeight: 1.6
      };

  return (
    <Box sx={{ mb: 2 }}>
      {summary && (
        <>
          <Typography
            variant={compact ? 'body2' : 'body1'}
            color="text.secondary"
            sx={bodySx}
          >
            {linkifyText(summaryText)}
          </Typography>

          {shouldTruncate && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={onToggleSummary}
              >
                {expanded ? 'Show less' : 'Show more'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {aiSummary && (
        <Box sx={{ mt: summary ? 1.5 : 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
          >
            AI-generated summary
          </Typography>
          <Typography
            variant={compact ? 'body2' : 'body1'}
            color="text.secondary"
            sx={bodySx}
          >
            {linkifyText(aiSummary)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
